import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Table, Tooltip, Switch } from 'antd';
import { FiEdit2, FiTrash2, FiPlus, FiMapPin, FiGrid } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import CounterFormModal from './counterFormModal';
import CounterTimeline from './counterTimeline';
import ConfirmModal from './confirmModal';
import { SectionCard, PageLoader, ErrorState, EmptyState } from './common';
import { CenterSelect } from './fields';
import { COUNTER_GENDERS, counterGenderLabel, formatTimeRange, toCenterId, getErrorMessage } from './helpers';

const genderClass = (gender) => `is-${(gender || 'both').toLowerCase()}`;

const toRequest = (counter, changes = {}) => ({
    name: counter.name,
    gender: counter.gender,
    startTime: counter.startTime,
    endTime: counter.endTime,
    breakFrom: counter.breakFrom || null,
    breakTo: counter.breakTo || null,
    sortOrder: counter.sortOrder,
    isActive: counter.isActive,
    ...changes
});

const CountersTab = ({ centers, centersLoading, centersError, onRetryCenters, refreshKey, onSaved, preferredCenter }) => {
    const [centerId, setCenterId] = useState('');
    const [counters, setCounters] = useState([]);
    const [slotMinutes, setSlotMinutes] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [modal, setModal] = useState({ show: false, counter: null });
    const [deleteState, setDeleteState] = useState({ counter: null, loading: false, error: '' });
    const [togglingId, setTogglingId] = useState(null);
    const requestId = useRef(0);

    useEffect(() => {
        if (centerId === '' && centers.length) setCenterId(String(centers[0].value));
    }, [centers, centerId]);

    useEffect(() => {
        if (preferredCenter) setCenterId(String(preferredCenter));
    }, [preferredCenter]);

    const loadCounters = useCallback(() => {
        if (centerId === '') return;
        const current = ++requestId.current;
        const id = toCenterId(centerId);
        setLoading(true);
        setLoadError('');
        const minutesRequest = slotSettingActions.getByCenter(id)
            .then(res => res?.effective?.slotMinutes ?? null)
            .catch(() => null);
        Promise.all([slotSettingActions.getCounters(id), minutesRequest]).then(([list, minutes]) => {
            if (current !== requestId.current) return;
            setCounters(Array.isArray(list) ? list : []);
            setSlotMinutes(minutes);
            setLoading(false);
        }).catch(err => {
            if (current !== requestId.current) return;
            setCounters([]);
            setLoading(false);
            setLoadError(getErrorMessage(err, 'Unable to load counters'));
        });
    }, [centerId]);

    useEffect(() => {
        loadCounters();
    }, [loadCounters, refreshKey]);

    const centerName = centers.find(c => String(c.value) === centerId)?.label || '';

    const afterChange = (res) => {
        loadCounters();
        onSaved(res);
    };

    const handleSaved = (res) => {
        setModal({ show: false, counter: null });
        afterChange(res);
    };

    const toggleActive = (counter, isActive) => {
        setTogglingId(counter.id);
        slotSettingActions.updateCounter(counter.id, toRequest(counter, { isActive })).then(res => {
            setTogglingId(null);
            toastMessage('success', `Counter ${counter.name} ${isActive ? 'activated' : 'deactivated'}`);
            afterChange(res);
        }).catch(err => {
            setTogglingId(null);
            toastMessage('error', getErrorMessage(err, `Unable to update counter ${counter.name}`));
        });
    };

    const handleDelete = () => {
        const { counter } = deleteState;
        setDeleteState({ counter, loading: true, error: '' });
        slotSettingActions.deleteCounter(counter.id).then(res => {
            setDeleteState({ counter: null, loading: false, error: '' });
            toastMessage('success', `Counter ${counter.name} deleted`);
            afterChange(res);
        }).catch(err => {
            setDeleteState({ counter, loading: false, error: getErrorMessage(err, 'Unable to delete the counter. Please try again.') });
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            render: (name, counter) => (
                <span className={`ss-title-cell ${counter.isActive ? '' : 'text-muted'}`}>{name}</span>
            )
        },
        {
            title: 'Serves',
            dataIndex: 'gender',
            filters: COUNTER_GENDERS.map(g => ({ text: g.label, value: g.value })),
            onFilter: (value, counter) => counter.gender === value,
            render: gender => <span className={`ss-gender-pill ${genderClass(gender)}`}>{counterGenderLabel(gender)}</span>
        },
        {
            title: 'Hours',
            key: 'hours',
            render: (_, counter) => <span className="ss-date-range">{formatTimeRange(counter.startTime, counter.endTime)}</span>
        },
        {
            title: 'Break',
            key: 'break',
            render: (_, counter) => (counter.breakFrom && counter.breakTo
                ? <span className="ss-pill is-custom">{formatTimeRange(counter.breakFrom, counter.breakTo)}</span>
                : <span className="ss-cell-sub">No break</span>)
        },
        {
            title: 'Order',
            dataIndex: 'sortOrder',
            align: 'center',
            width: 80
        },
        {
            title: 'Active',
            key: 'active',
            align: 'center',
            width: 90,
            render: (_, counter) => (
                <Tooltip title={counter.isActive ? 'Switch off to stop its slots after the last booked day' : 'Switch on to bring its slots back'}>
                    <Switch size="small" checked={counter.isActive} loading={togglingId === counter.id}
                        disabled={!!togglingId} aria-label={`${counter.name} active`}
                        onChange={checked => toggleActive(counter, checked)} />
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            width: 110,
            render: (_, counter) => (
                <div className="d-flex justify-content-end gap-2">
                    <Tooltip title="Edit">
                        <button type="button" className="ss-icon-btn" aria-label={`Edit ${counter.name}`}
                            onClick={() => setModal({ show: true, counter })}>
                            <FiEdit2 />
                        </button>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <button type="button" className="ss-icon-btn is-danger" aria-label={`Delete ${counter.name}`}
                            onClick={() => setDeleteState({ counter, loading: false, error: '' })}>
                            <FiTrash2 />
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ];

    const renderBody = () => {
        if (centersLoading) return <PageLoader text="Loading centers..." />;
        if (centersError) return <ErrorState title="Could not load centers" message={centersError} onRetry={onRetryCenters} />;
        if (!centers.length) return <EmptyState icon={<FiMapPin />} title="No active centers" text="There are no active centers to set up." />;
        if (loadError) return <ErrorState message={loadError} onRetry={loadCounters} />;
        return (
            <Table
                className="ss-antd-table"
                rowKey="id"
                columns={columns}
                dataSource={counters}
                loading={loading}
                pagination={false}
                scroll={{ x: 760 }}
                showSorterTooltip={false}
                rowClassName={counter => (counter.isActive ? '' : 'ss-row-inactive')}
                locale={{
                    emptyText: loading ? ' ' : (
                        <EmptyState
                            icon={<FiGrid />}
                            title="No counters yet"
                            text="Without counters this center keeps its normal seats per slot. With counters, each counter open at a time adds one seat to that time."
                            action={<Button variant="primary" size="sm" className="mt-2" onClick={() => setModal({ show: true, counter: null })}><FiPlus /> Add counter</Button>}
                        />
                    )
                }}
            />
        );
    };

    const activeCount = counters.filter(c => c.isActive).length;

    return (
        <>
            <SectionCard>
                <div className="ss-counter-toolbar">
                    <div className="ss-counter-center">
                        <Form.Label htmlFor="ct-center">Center</Form.Label>
                        <CenterSelect id="ct-center" value={centerId} centers={centers} loading={centersLoading}
                            disabled={centersLoading || !centers.length} onChange={setCenterId} />
                    </div>
                    <div className="ss-counter-meta">
                        {slotMinutes && <span className="ss-pill is-primary">Slot duration: {slotMinutes} min</span>}
                        {counters.length > 0 && <span className="ss-pill is-muted">{activeCount} of {counters.length} active</span>}
                    </div>
                    <Button variant="primary" disabled={!centerId || loading || !!loadError} onClick={() => setModal({ show: true, counter: null })}>
                        <FiPlus /> Add counter
                    </Button>
                </div>
            </SectionCard>

            {!loading && !loadError && counters.length > 0 && (
                <SectionCard title="The day at a glance" subtitle="Each bar is a counter's working hours (a gap is its break). The Seats row shows how many people can book each slot. Click a counter to edit it.">
                    <CounterTimeline counters={counters} slotMinutes={slotMinutes} onSelect={counter => setModal({ show: true, counter })} />
                    <div className="ss-ctl-legend">
                        <span className="ss-ctl-key" style={{ '--key': '#3b6fe0' }}>Male</span>
                        <span className="ss-ctl-key" style={{ '--key': '#c2306f' }}>Female</span>
                        <span className="ss-ctl-key" style={{ '--key': 'var(--ss-open)' }}>Anyone (Both)</span>
                        <span className="ss-ctl-key" style={{ '--key': '#c9cfdb' }}>Inactive</span>
                    </div>
                </SectionCard>
            )}

            <SectionCard
                title={centerName ? `Counters · ${centerName}` : 'Counters'}
                subtitle="Seats per time = active counters open at that time (1 counter = 1 seat)."
                bodyClassName="p-0"
            >
                {renderBody()}
            </SectionCard>

            <CounterFormModal
                show={modal.show}
                counter={modal.counter}
                centerId={toCenterId(centerId)}
                centerName={centerName}
                slotMinutes={slotMinutes}
                onClose={() => setModal({ show: false, counter: null })}
                onSaved={handleSaved}
            />

            <ConfirmModal
                show={!!deleteState.counter}
                title="Delete counter"
                message={deleteState.counter && <>Delete counter <strong>{deleteState.counter.name}</strong>? Seats at its times go down by one. Existing bookings are kept.</>}
                confirmLabel="Delete"
                loading={deleteState.loading}
                error={deleteState.error}
                onConfirm={handleDelete}
                onClose={() => setDeleteState({ counter: null, loading: false, error: '' })}
            />
        </>
    );
};

export default CountersTab;
